import { motion, useInView } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Navbar, Footer } from "@/components/site-chrome";
import { HeroVisual } from "@/components/landing/hero-visual";

const PAPER = "#F8FAFC";
const FOREST = "#0A1628";
const FOREST_MID = "#3B82F6";
const FOREST_DEEP = "#050D1A";
const GOLD = "#F59E0B";
const INK = "#0A1628";
const MUTED = "#64748B";
const AQUA = "#3B82F6";
const CORAL = "#FB7185";
const EASE = [0.16, 1, 0.3, 1] as const;

const EXPLORE_CARDS = [
  {
    num: "01",
    title: "The Experience",
    desc: "See exactly what parents and schools see. Explore the full platform from both sides — the parent's pocket and the school's control centre.",
    href: "/experience",
    color: FOREST_MID,
    accent: AQUA,
    icon: "experience",
  },
  {
    num: "02",
    title: "The Implementation",
    desc: "From paperwork to platform in five weeks. Walk through the full journey — mapping, migration, activation, launch, and optimisation.",
    href: "/five-weeks",
    color: GOLD,
    accent: CORAL,
    icon: "implementation",
  },
  {
    num: "03",
    title: "The Ecosystem",
    desc: "Eight connected layers, one learner identity. Explore the full architecture — identity, learning, campus, transit, pay, connect, intelligence, government.",
    href: "/ecosystem",
    color: FOREST,
    accent: AQUA,
    icon: "ecosystem",
  },
  {
    num: "04",
    title: "Why Fundred",
    desc: "Stop chasing information across spreadsheets, WhatsApp groups and paper registers. See how Fundred brings every signal into one connected platform.",
    href: "/why-fundred",
    color: FOREST_MID,
    accent: CORAL,
    icon: "why",
  },
  {
    num: "05",
    title: "Real-Time Insights",
    desc: "Watch collections flow, track revenue trends, predict outcomes, and measure the pulse of your institution — live. No spreadsheets. No quarterly reports.",
    href: "/insights",
    color: FOREST,
    accent: GOLD,
    icon: "insights",
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: PAPER, fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <Navbar />
      <HeroVisual />
      <Footer />
    </div>
  );
}

export default LandingPage;

// ════════════════════════════════════════════════════════════
// EXPLORE SECTION — Cinematic gateway to standalone pages
// ════════════════════════════════════════════════════════════
function ExploreSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section ref={ref} className="relative overflow-hidden py-20 sm:py-32" style={{ background: FOREST_DEEP }}>
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 50% at 50% 50%, ${GOLD}08 0%, transparent 60%)` }} />

      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(${GOLD}44 1px, transparent 1px), linear-gradient(90deg, ${GOLD}44 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
        }}
      />

      {/* Floating ambient orbs */}
      <motion.div
        animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute left-10 top-20 h-40 w-40 rounded-full opacity-10 blur-3xl"
        style={{ background: GOLD }}
      />
      <motion.div
        animate={{ x: [0, -15, 0], y: [0, 10, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="pointer-events-none absolute right-10 bottom-10 h-48 w-48 rounded-full opacity-08 blur-3xl"
        style={{ background: AQUA }}
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: EASE }}
          className="text-center"
        >
          <div
            className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
            style={{ borderColor: `${GOLD}30`, background: `${GOLD}08` }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: GOLD }}>
              Explore the Platform
            </span>
          </div>
          <h2
            className="text-3xl font-bold tracking-tight sm:text-5xl"
            style={{ color: "#fff", letterSpacing: "-0.04em", lineHeight: 1.02 }}
          >
            Five ways to discover
          </h2>
          <h2
            className="text-3xl font-bold tracking-tight sm:text-5xl"
            style={{ color: GOLD, letterSpacing: "-0.04em", lineHeight: 1.02, fontStyle: "italic" }}
          >
            what Fundred can do.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>
            Each page is an immersive experience. Dive into the parent and school journeys, the five-week implementation, the full ecosystem architecture, why Fundred exists, or real-time financial intelligence.
          </p>
        </motion.div>

        {/* Cards — responsive grid */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {EXPLORE_CARDS.map((card, i) => (
            <motion.div
              key={card.num}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6, ease: EASE }}
              onHoverStart={() => setHoveredCard(i)}
              onHoverEnd={() => setHoveredCard(null)}
              className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}
            >
              <Link to={card.href} className="group block h-full">
                <div
                  className="relative h-full overflow-hidden rounded-2xl border p-6 transition-all duration-400"
                  style={{
                    borderColor: hoveredCard === i ? `${card.accent}50` : "rgba(255,255,255,0.08)",
                    background: hoveredCard === i ? `${card.color}15` : "rgba(255,255,255,0.03)",
                    boxShadow: hoveredCard === i ? `0 20px 48px -12px ${card.accent}25` : "0 4px 24px -8px rgba(0,0,0,0.3)",
                    transform: hoveredCard === i ? "translateY(-6px)" : "translateY(0)",
                  }}
                >
                  {/* Number watermark */}
                  <span
                    className="absolute -right-2 -top-4 text-7xl font-black opacity-[0.06] transition-opacity duration-400 group-hover:opacity-[0.12]"
                    style={{ color: card.accent, letterSpacing: "-0.06em" }}
                  >
                    {card.num}
                  </span>

                  {/* Icon */}
                  <div
                    className="relative grid h-14 w-14 place-items-center rounded-2xl transition-transform duration-400 group-hover:scale-110"
                    style={{
                      background: `${card.accent}15`,
                      border: `1px solid ${card.accent}30`,
                    }}
                  >
                    <ExploreIcon name={card.icon} color={card.accent} />
                  </div>

                  {/* Title */}
                  <h3
                    className="mt-5 text-xl font-bold tracking-tight transition-colors"
                    style={{ color: "#fff", letterSpacing: "-0.02em" }}
                  >
                    {card.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-2.5 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
                    {card.desc}
                  </p>

                  {/* CTA arrow */}
                  <div className="mt-6 flex items-center gap-2">
                    <span
                      className="text-[11px] font-bold uppercase tracking-[0.15em] transition-colors"
                      style={{ color: hoveredCard === i ? card.accent : "rgba(255,255,255,0.4)" }}
                    >
                      Explore
                    </span>
                    <motion.span
                      animate={{ x: hoveredCard === i ? 6 : 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className="flex h-6 w-6 items-center justify-center rounded-lg"
                      style={{
                        background: hoveredCard === i ? `${card.accent}20` : "rgba(255,255,255,0.06)",
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke={hoveredCard === i ? card.accent : "rgba(255,255,255,0.4)"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </motion.span>
                  </div>

                  {/* Bottom accent line */}
                  <div
                    className="absolute bottom-0 left-0 h-0.5 transition-all duration-500"
                    style={{
                      background: card.accent,
                      width: hoveredCard === i ? "100%" : "0%",
                    }}
                  />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExploreIcon({ name, color }: { name: string; color: string }) {
  if (name === "experience") {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    );
  }
  if (name === "implementation") {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (name === "ecosystem") {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <circle cx="5" cy="5" r="2" />
        <circle cx="19" cy="5" r="2" />
        <circle cx="5" cy="19" r="2" />
        <circle cx="19" cy="19" r="2" />
        <path d="M7 7l3 3M17 7l-3 3M7 17l3-3M17 17l-3-3" />
      </svg>
    );
  }
  if (name === "why") {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-5" />
    </svg>
  );
}
